import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { IQuestionListItem, NoteItem } from '@/types';
import './index.less';
import { addOrCancelNote, addUpdateNote, deleteNote, getNoteList } from '@/apis';
import { Checkbox, Input, message } from 'antd';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';

interface IProps {
    questionItem: IQuestionListItem;
    paperDataId?: number;
}

const QuestionAnswerAndNote: React.FC<IProps> = props => {
    const { questionItem, paperDataId } = props;
    const { projectId, subjectId, questionId } = questionItem;
    const { t } = useTranslation();

    const [activeTab, setActiveKey] = useState<'note' | 'answerSquare'>('note');

    const [noteList, setNoteList] = useState<NoteItem[]>([]);
    const [inputValue, setInputValue] = useState<string>();
    const [isOpen, setIsOpen] = useState(false); // 是否公开笔记
    const [isShowNote, setIsShowNote] = useState(true);
    const yourSelfNote = useRef<NoteItem>();

    const reqNoteList = async () => {
        const ret = await getNoteList({
            questionId,
        });
        if (ret.status === 0) {
            const list = ret.result;
            yourSelfNote.current = list.find((note: { isSelf: any }) => note.isSelf);
            if (yourSelfNote.current) {
                setIsShowNote(false);
            }
            setNoteList(list);
        }
    };
    useEffect(() => {
        reqNoteList();
    }, [questionId]);

    const changeSelfNote = () => {
        setIsShowNote(true);
        setIsOpen(yourSelfNote.current?.noteState === 0 ? true : false);
        setInputValue(DOMPurify.sanitize(yourSelfNote.current?.noteContent || ''));
    };

    const deleteSelfNote = async () => {
        const ret = await deleteNote(yourSelfNote.current!.noteId);
        if (ret.status === 0) {
            message.success(t('plaza.removeNote'));
            yourSelfNote.current = undefined;
            setInputValue(undefined);
            reqNoteList();
            setIsShowNote(true);
        }
    };

    const handleSaveNote = async () => {
        const ret = await addUpdateNote({
            isSelf: isOpen ? 0 : 1,
            noteContent: inputValue!,
            noteId: yourSelfNote.current?.noteId || undefined,
            paperDataId: paperDataId,
            projectId: projectId,
            questionId: questionId,
            subjectId: subjectId, // 科目ID
        });
        if (ret.status === 0) {
            setIsShowNote(false);
            message.success(yourSelfNote.current?.noteId ? t('plaza.editSuccess') : t('plaza.addSuccess'));
            reqNoteList();
        }
    };

    const onChangeInputValue = (e: any) => {
        setInputValue(DOMPurify.sanitize(e.target.value));
    };

    const addCancelLike = async (noteItem: NoteItem) => {
        const ret = await addOrCancelNote({
            isFabulous: !!noteItem.isFabulous,
            noteId: noteItem.noteId,
            projectId,
            questionId,
            subjectId,
        });
        if (ret.status === 0) {
            message.success(noteItem.isFabulous ? t('plaza.cancelLike') : t('plaza.likeSuccess'));
            reqNoteList();
        }
    };

    return (
        <div className="sub-tiku__question-plaza-container">
            <div className="sub-tiku__plaza-tab-box">
                <div
                    className={classNames('sub-tiku__question-note-tab', {
                        'sub-tiku__active-tab': activeTab === 'note',
                    })}
                    onClick={() => {
                        setActiveKey('note');
                    }}
                >
                    <div />
                    {t('plaza.noteTab')}
                </div>
            </div>

            <div
                className={classNames({
                    'sub-tiku__active-note': activeTab === 'note',
                    'sub-tiku__in-active-note': activeTab !== 'note',
                })}
            >
                <div>
                    {isShowNote && (
                        <>
                            <Input.TextArea
                                autoSize={{ minRows: 4 }}
                                maxLength={3000}
                                onChange={onChangeInputValue}
                                placeholder={t('plaza.notePlaceholder')}
                                value={inputValue}
                            />
                            <div className="sub-tiku__sendCommentActions">
                                <div style={{ marginRight: 24 }}>
                                    <Checkbox checked={isOpen} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIsOpen(e.target.checked)} /> {t('plaza.isPublic')}
                                </div>
                                <div
                                    className={classNames('sub-tiku__tiku-default-btn', { 'sub-tiku__daisabled': !inputValue })}
                                    onClick={handleSaveNote}
                                >
                                    {t('plaza.submitNote')}
                                </div>
                            </div>
                        </>
                    )}
                    {yourSelfNote.current && (
                        <div className="sub-tiku__commentItem" key={yourSelfNote.current.noteId}>
                            <div className="sub-tiku__commentHead">
                                <div
                                    className="sub-tiku__headImg"
                                    style={{
                                        backgroundImage: `url(${
                                            yourSelfNote.current.studentImage || '//simg01.gaodunwangxiao.com/v/Uploads/avatar/default.jpg'
                                        })`,
                                    }}
                                />
                            </div>
                            <div className="sub-tiku__commentContentBox">
                                <div>
                                    <div className="sub-tiku__commentUser">{t('plaza.myNote')}</div>
                                    <div className="sub-tiku__question-note-like-box">
                                        <div
                                            className={classNames({
                                                'sub-tiku__question-note-like': yourSelfNote.current.isFabulous,
                                                'sub-tiku__question-note-not-like': !yourSelfNote.current.isFabulous,
                                            })}
                                            onClick={() => addCancelLike(yourSelfNote.current!)}
                                        />
                                        <span className="sub-tiku__likeNum">{yourSelfNote.current.fabulousNum}</span>
                                    </div>
                                </div>
                                <div
                                    className="sub-tiku__commentContentText"
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(yourSelfNote.current.noteContent),
                                    }}
                                />
                                <div>
                                    <div
                                        style={{ color: 'rgba(0,0,0,0.34)' }}
                                        className="sub-tiku__tiku-default-btn sub-tiku__tiku-text-btn"
                                        onClick={() => deleteSelfNote()}
                                    >
                                        {t('actions.delete')}
                                    </div>
                                    <div className="sub-tiku__tiku-default-btn sub-tiku__tiku-text-btn" onClick={() => changeSelfNote()}>
                                        {t('actions.edit')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {noteList.map(
                        item =>
                            !item.isSelf && (
                                <div className="sub-tiku__commentItem" key={item.noteId}>
                                    <div className="sub-tiku__commentHead">
                                        <div
                                            className="sub-tiku__headImg"
                                            style={{
                                                backgroundImage: `url(${
                                                    item.studentImage || '//simg01.gaodunwangxiao.com/v/Uploads/avatar/default.jpg'
                                                })`,
                                            }}
                                        />
                                    </div>
                                    <div className="sub-tiku__commentContentBox">
                                        <div>
                                            <div className="sub-tiku__commentUser">{item.studentName}</div>
                                            <div className="sub-tiku__question-note-like-box">
                                                <div
                                                    className={classNames({
                                                        'sub-tiku__question-note-like': item.isFabulous,
                                                        'sub-tiku__question-note-not-like': !item.isFabulous,
                                                    })}
                                                    onClick={() => addCancelLike(item)}
                                                />
                                                <span className="sub-tiku__likeNum">{item.fabulousNum}</span>
                                            </div>
                                        </div>

                                        <div
                                            className="sub-tiku__commentContentText"
                                            dangerouslySetInnerHTML={{
                                                __html: DOMPurify.sanitize(item.noteContent),
                                            }}
                                        />
                                    </div>
                                </div>
                            )
                    )}
                </div>
            </div>
        </div>
    );
};

export default QuestionAnswerAndNote;
